import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Copy, Plus, Link2, Trash2, CheckCircle2, Clock, XCircle, Send, Mail, AlertTriangle, Search, Award, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { CourseMultiSelect } from "@/components/admin/CourseMultiSelect";
import { EmailCertificateDialog } from "@/components/admin/EmailCertificateDialog";

const EMPTY_FORM = { candidate_name: "", candidate_email: "", course_ids: [] };

function reportBulkResult({ created, skipped, email_failed }) {
  const emailed = created.filter((c) => c.emailed).length;
  if (created.length) toast.success(`Enrolled on ${created.length} course${created.length > 1 ? "s" : ""}; ${emailed} email${emailed === 1 ? "" : "s"} sent`);
  if (skipped.length) toast.info(`Skipped (already enrolled): ${skipped.map((s) => s.course_title || s.course_id).join(", ")}`, { duration: 10000 });
  if (email_failed.length) toast.error(`Email failed for: ${email_failed.map((f) => f.course_title).join(", ")}: ${email_failed[0].error}`, { duration: 12000 });
}

const STATUS_META = {
  pending: { label: "Not started", icon: Clock, cls: "bg-secondary text-muted-foreground" },
  in_progress: { label: "In progress", icon: Clock, cls: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  completed: { label: "Completed", icon: CheckCircle2, cls: "bg-foreground text-background" },
  quiz_failed: { label: "Quiz failed", icon: XCircle, cls: "bg-destructive/10 text-destructive border-destructive/30" },
};

export default function CandidatesPage() {
  const [invitations, setInvitations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [certTarget, setCertTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [inv, cs] = await Promise.all([api.get("/invitations"), api.get("/courses")]);
        if (cancelled) return;
        setInvitations(inv.data);
        setCourses(cs.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [reload]);
  const refresh = () => setReload((n) => n + 1);

  // Live poll — pull invitation progress every 10 s so admins can watch a candidate
  // move through modules / the SCORM SCO in real time.
  useEffect(() => {
    const id = setInterval(() => setReload((n) => n + 1), 10000);
    return () => clearInterval(id);
  }, []);

  const baseUrl = useMemo(() => `${window.location.origin}/learn`, []);

  const filteredInvitations = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invitations.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        i.candidate_name,
        i.candidate_email,
        i.course_title,
        (STATUS_META[i.status] || {}).label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [invitations, query, statusFilter]);

  const downloadCertificate = (inv) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    // The certificate endpoint uses the candidate's token — no admin auth required.
    // We open it in a new tab; the Content-Disposition header makes it a download.
    window.open(`${base}/api/candidate/certificate?token=${encodeURIComponent(inv.token)}`, "_blank", "noopener");
  };

  const create = async () => {
    if (!form.candidate_name || !form.candidate_email || form.course_ids.length === 0) {
      toast.error("Enter a name, email and at least one course");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/invitations/bulk", form);
      setForm(EMPTY_FORM);
      setOpen(false);
      reportBulkResult(r.data);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not create invitation"));
    } finally {
      setBusy(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingInv, setDeletingInv] = useState(false);

  const remove = async () => {
    if (!deleteTarget) return;
    setDeletingInv(true);
    try {
      await api.delete(`/invitations/${deleteTarget.id}`);
      toast.success(`Deleted invitation for ${deleteTarget.name}`);
      setDeleteTarget(null);
      refresh();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingInv(false);
    }
  };

  const copy = async (token) => {
    const url = `${baseUrl}/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — link: " + url);
    }
  };

  const sendInvite = async (inv) => {
    try {
      await api.post(`/invitations/${inv.id}/send`);
      toast.success(`Invite emailed to ${inv.candidate_email}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Send failed"));
      refresh(); // pull the stored last_send_error so the row shows it
    }
  };

  const toggleReminders = async (inv) => {
    try {
      await api.put(`/invitations/${inv.id}/reminders`, { enabled: !!inv.reminders_off });
      toast.success(inv.reminders_off ? "Reminder turned on" : "Reminder turned off");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not update reminder"));
    }
  };

  const [testing, setTesting] = useState(false);
  const runEmailTest = async () => {
    setTesting(true);
    const tid = toast.loading("Sending diagnostic test email…");
    try {
      const r = await api.post("/invitations/email-test");
      toast.success(
        `Test sent to ${r.data.sent_to} from ${r.data.sent_from}. Check that inbox and, if it arrives, magic-link invites will work too.`,
        { id: tid, duration: 8000 }
      );
    } catch (e) {
      toast.error(
        formatApiError(e.response?.data?.detail, "Test email failed"),
        { id: tid, duration: 12000 }
      );
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-3">/ Candidates</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Magic links</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Create a candidate, copy their unique link, and send it via your usual channel (email, Slack…).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={runEmailTest}
            disabled={testing}
            data-testid="test-email-button"
            className="rounded-sm h-11"
            title="Send a diagnostic test email to your own admin address so you can confirm the magic-link email pipeline works before inviting real candidates."
          >
            <Send size={14} strokeWidth={1.5} className="mr-2" />
            {testing ? "Testing…" : "Test email delivery"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-invitation-button" className="rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90">
                <Plus size={16} strokeWidth={1.5} className="mr-2" /> Invite candidate
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black tracking-tighter">Invite a candidate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Name</Label>
                <Input data-testid="invite-name-input" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} className="mt-2 rounded-sm h-11" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Email</Label>
                <Input data-testid="invite-email-input" type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} className="mt-2 rounded-sm h-11" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">
                  Courses {form.course_ids.length > 0 && <span className="text-muted-foreground">({form.course_ids.length} selected)</span>}
                </Label>
                <CourseMultiSelect courses={courses} selected={form.course_ids} onChange={(ids) => setForm({ ...form, course_ids: ids })} />
                <p className="mt-2 text-[11px] text-muted-foreground">Each course gets its own link, emailed to the candidate separately.</p>
              </div>
              <Button onClick={create} disabled={busy} data-testid="invite-submit-button" className="w-full rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90">
                <Send size={14} strokeWidth={1.5} className="mr-2" /> {busy ? "Enrolling..." : "Enrol & email links"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {invitations.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <Link2 size={32} strokeWidth={1.2} className="mx-auto text-muted-foreground" />
          <h3 className="mt-6 text-xl font-semibold tracking-tight">No candidates yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">Invite your first candidate to generate a magic link.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="candidates-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or course…"
                className="pl-9 rounded-sm h-10"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  data-testid="candidates-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  title="Clear search"
                >
                  <XCircle size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="candidates-status-filter" className="w-44 rounded-sm h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Not started</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="quiz_failed">Quiz failed</SelectItem>
              </SelectContent>
            </Select>
            <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground" data-testid="candidates-count">
              {filteredInvitations.length} / {invitations.length}
            </span>
          </div>

          {filteredInvitations.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center">
              <Search size={24} strokeWidth={1.2} className="mx-auto text-muted-foreground" />
              <div className="mt-3 font-mono text-xs tracking-widest uppercase text-muted-foreground">No matches</div>
              <p className="mt-2 text-sm text-muted-foreground">Nothing matches &ldquo;{query}&rdquo; in {statusFilter === "all" ? "any status" : `status ${statusFilter}`}.</p>
            </div>
          ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary border-b border-border">
              <tr className="text-left font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                <th className="p-4">Candidate</th>
                <th className="p-4">Course</th>
                <th className="p-4">Status</th>
                <th className="p-4 min-w-[180px]">Progress</th>
                <th className="p-4">Quiz</th>
                <th className="p-4">Link &amp; invite</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filteredInvitations.map((inv) => {
                const meta = STATUS_META[inv.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <tr key={inv.id} data-testid={`invitation-row-${inv.id}`} className="border-b border-border last:border-b-0">
                    <td className="p-4">
                      <div className="font-semibold">{inv.candidate_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{inv.candidate_email}</div>
                    </td>
                    <td className="p-4 max-w-xs">
                      <div className="truncate">{inv.course_title || "—"}</div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border ${meta.cls}`}>
                        <Icon size={12} strokeWidth={1.5} /> {meta.label}
                      </span>
                    </td>
                    <td className="p-4">
                      <ProgressCell inv={inv} />
                    </td>
                    <td className="p-4 font-mono text-xs">
                      {inv.quiz_score == null ? "—" : `${inv.quiz_score}%`}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => copy(inv.token)}
                          data-testid={`copy-link-${inv.id}`}
                          className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
                          title={`${baseUrl}/${inv.token}`}
                        >
                          <Copy size={12} /> Copy link
                        </button>
                        <button
                          onClick={() => sendInvite(inv)}
                          data-testid={`send-invite-${inv.id}`}
                          className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
                          title="Email this magic link to the candidate"
                        >
                          <Mail size={12} /> {inv.send_count > 0 ? `Resend (${inv.send_count})` : "Send invite"}
                        </button>
                        {inv.status === "completed" && (
                          <button
                            onClick={() => downloadCertificate(inv)}
                            data-testid={`download-cert-${inv.id}`}
                            className="inline-flex items-center gap-2 font-mono text-xs text-foreground hover:opacity-80 transition-colors"
                            title={`Download certificate for ${inv.candidate_name}`}
                          >
                            <Award size={12} /> Certificate
                          </button>
                        )}
                        {inv.status === "completed" && (
                          <button
                            onClick={() => setCertTarget(inv)}
                            data-testid={`email-cert-${inv.id}`}
                            className="inline-flex items-center gap-2 font-mono text-xs text-foreground hover:opacity-80 transition-colors"
                            title="Email the certificate to the candidate or a client"
                          >
                            <Mail size={12} /> Email certificate
                          </button>
                        )}
                      </div>
                      {inv.last_sent_at && (
                        <div className="mt-1 font-mono text-[10px] text-muted-foreground" data-testid={`last-sent-${inv.id}`}>
                          Last sent: {new Date(inv.last_sent_at).toLocaleString()}
                        </div>
                      )}
                      {inv.status !== "completed" && (
                        <button
                          onClick={() => toggleReminders(inv)}
                          data-testid={`reminders-toggle-${inv.id}`}
                          className="mt-1 inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                          title="Turn the automatic 3-day reminder on or off for this candidate"
                        >
                          {inv.reminders_off ? <BellOff size={11} /> : <Bell size={11} />}
                          {inv.reminder_sent_at
                            ? `Reminder sent ${new Date(inv.reminder_sent_at).toLocaleDateString()}`
                            : inv.reminders_off ? "Reminder off" : "Reminder on"}
                        </button>
                      )}
                      {inv.last_send_error && (
                        <div
                          data-testid={`send-error-${inv.id}`}
                          className="mt-1 inline-flex items-start gap-1.5 text-[10px] font-mono text-destructive max-w-xs"
                          title={inv.last_send_error}
                        >
                          <AlertTriangle size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                          <span className="truncate">{inv.last_send_error}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget({ id: inv.id, name: inv.candidate_name })} data-testid={`delete-invitation-${inv.id}`}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}

      <EmailCertificateDialog inv={certTarget} onClose={() => setCertTarget(null)} onSent={refresh} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-invitation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black tracking-tighter">Delete invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the magic-link invitation for <span className="font-semibold text-foreground">{deleteTarget?.name || "this candidate"}</span>. Their link will stop working immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-invitation-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deletingInv}
              data-testid="delete-invitation-confirm"
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingInv ? "Deleting…" : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const SCORM_STATUS_STYLE = {
  passed:      "bg-foreground text-background border-foreground",
  completed:   "bg-foreground text-background border-foreground",
  incomplete:  "bg-yellow-50 text-yellow-800 border-yellow-200",
  failed:      "bg-destructive/10 text-destructive border-destructive/30",
  "not attempted": "bg-secondary text-muted-foreground border-border",
};

function ProgressCell({ inv }) {
  const p = inv.progress || {};
  const total = p.modules_total || 0;
  const viewed = p.modules_viewed || 0;
  const pct = p.percent ?? 0;
  const scormStatus = p.scorm_latest_status;
  const scormStyle = SCORM_STATUS_STYLE[scormStatus] || SCORM_STATUS_STYLE["not attempted"];

  if (total === 0) {
    return <span className="font-mono text-xs text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[160px]" data-testid={`progress-cell-${inv.id}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-secondary rounded-sm overflow-hidden">
          <div
            className="h-full bg-foreground transition-all"
            style={{ width: `${pct}%` }}
            data-testid={`progress-bar-${inv.id}`}
          />
        </div>
        <span className="font-mono text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
          {pct}%
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[10px] text-muted-foreground">
          {viewed}/{total} modules
        </span>
        {p.has_scorm && scormStatus && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${scormStyle}`}
            data-testid={`scorm-status-${inv.id}`}
            title={`SCORM ${p.scorm_passed_count}/${p.scorm_modules_total} passed`}
          >
            SCORM: {scormStatus}
            {p.scorm_latest_score != null && <> · {p.scorm_latest_score}</>}
          </span>
        )}
      </div>
    </div>
  );
}

